import React from 'react'
import { Link } from 'react-router-dom'
import LocalHelmet from '../common/LocalHelmet'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { 
  FiTarget, 
  FiUsers, 
  FiHeart, 
  FiSquare, 
  FiTool, 
  FiArrowRight 
} = FiIcons

const About = () => {
  const headshotUrl = "https://media-manager-c.questera.ai/greta-media/55facf8b796fb9825ee39ccbab33621cc9c5386a2f16c2d23981016962abefa8e594f215c136dad74ce09bc699797373/images/aW1hZ2UvanBlZw==/eccda0d860a75dfab1cb1c7805383251.jpeg"

  return (
    <>
      <LocalHelmet prioritizeSeoTags>
        <title>About | Richardson Strategy Group</title>
        <meta name="description" content="Meet Andrew Richardson. Andrew Richardson has spent nearly twenty years helping organizations work through growth challenges." />
        <link rel="canonical" href="https://richardsonstrategy.group/about" />
        <meta property="og:title" content="About | Richardson Strategy Group" />
        <meta property="og:description" content="Meet Andrew Richardson. Andrew Richardson has spent nearly twenty years helping organizations work through growth challenges." />
        <meta property="og:url" content="https://richardsonstrategy.group/about" />
        <meta property="og:image" content={headshotUrl} />
        <meta name="twitter:card" content="summary_large_image" />
      </LocalHelmet>

      {/* Header Section */}
      <div className="pt-20 pb-12 bg-white text-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6"
          >
            Meet Andrew Richardson
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg md:text-xl text-secondary-600 leading-relaxed max-w-3xl mx-auto"
          >
            Andrew Richardson has spent nearly twenty years helping organizations work through growth challenges. He has led strategy and analytics across Fortune 500 companies, private equity–backed firms, and fast-growing startups.
          </motion.p>
        </div>
      </div>

      {/* The Story Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-full lg:w-1/2"
            >
              <img 
                src={headshotUrl} 
                alt="Andrew Richardson" 
                className="w-full rounded-2xl shadow-lg object-cover bg-secondary-100" 
              />
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-full lg:w-1/2 flex flex-col justify-center"
            >
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">The Story</h2>
              <div className="prose prose-lg text-secondary-600 space-y-6">
                <p>
                  You want clarity. Andrew spent two decades helping leaders get there. Building teams, streamlining decisions, tackling tough board meetings. Whether you need a fresh strategy or just an honest answer, Richardson Strategy Group keeps things focused.
                </p>
                <p>
                  Today, as founder of Richardson Strategy Group, Andrew works with leadership teams to bring focus and clarity to decision-making. He also helps executives evaluate and build new technologies, including AI, and use them in practical ways that drive efficiency and competitive advantage. As a neurodivergent leader, father, and coach, Andrew brings both analytical rigor and human empathy to every engagement.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Why This Firm Exists Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-secondary-50 rounded-3xl p-8 md:p-12 shadow-sm border border-secondary-100"
          >
            <h2 className="text-2xl font-bold text-secondary-900 mb-8">Why This Firm Exists</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="mt-1 flex-shrink-0 text-primary-500">
                  <SafeIcon icon={FiTarget} className="w-6 h-6" />
                </div>
                <p className="text-lg text-secondary-700">
                  Too many consultants deliver reports that sit on shelves. We deliver living decision tools.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 flex-shrink-0 text-primary-500">
                  <SafeIcon icon={FiUsers} className="w-6 h-6" />
                </div>
                <p className="text-lg text-secondary-700">
                  Strategy without inclusion isn't strategy it's just optimization for the status quo.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="mt-1 flex-shrink-0 text-primary-500">
                  <SafeIcon icon={FiHeart} className="w-6 h-6" />
                </div>
                <p className="text-lg text-secondary-700">
                  Growth is messy, but clarity is worth the work. Especially when that work centers people.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Experience & Expertise */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900">Experience & Expertise</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Fortune 500 Analytics Leader",
                desc: "Scaled analytics teams of 125+, owned P&Ls, and delivered growth clarity to CMOs, CEOs, and Boards."
              },
              {
                title: "PE-Backed Growth Strategist",
                desc: "Built analytics divisions from scratch and guided transformation initiatives for private equity portfolio companies."
              },
              {
                title: "Startup Growth Advisor",
                desc: "Helped emerging companies establish data-driven decision frameworks and sustainable growth strategies."
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white border border-secondary-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-4">{item.title}</h3>
                <p className="text-secondary-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* What Drives This Work */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900">What Drives This Work</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: FiSquare,
                title: "Neurodivergent Leadership",
                desc: "Bringing unique perspectives and cognitive approaches to strategy and problem-solving."
              },
              {
                icon: FiUsers,
                title: "Inclusion Advocate",
                desc: "Championing neurodiversity, DEI, and LGBTQIA advocacy in every client engagement."
              },
              {
                icon: FiTool,
                title: "Maker's Mindset",
                desc: "Blending craft, curiosity, and precision into leadership and strategic thinking."
              },
              {
                icon: FiHeart,
                title: "Father & Coach",
                desc: "Understanding that great strategy requires both analytical rigor and human empathy."
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center"
              >
                <div className="mx-auto w-16 h-16 bg-primary-50 text-primary-500 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={item.icon} className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-secondary-900 mb-3">{item.title}</h3>
                <p className="text-secondary-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Philosophy */}
      <div className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-primary-50 rounded-3xl p-10 md:p-16 text-center shadow-sm border border-primary-100"
          >
            <h2 className="text-2xl font-bold text-secondary-900 mb-8">Our Philosophy</h2>
            <p className="text-xl md:text-2xl text-secondary-800 italic font-serif leading-relaxed mb-8">
              "We aren't an analytics vendor. We're a strategy partner. We combine data, AI, and leadership experience with a craftsman's precision and a coach's patience. Our clients leave meetings with new clarity, sharper questions, and sometimes even a smile."
            </p>
            <p className="text-lg text-secondary-600">
              We don't put up with intolerance or hate. Growth without humanity isn't growth we're interested in.
            </p>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-secondary-900 mb-4">
            Ready to Work Together?
          </h2>
          <p className="text-lg md:text-xl text-secondary-600 mb-10">
            Let's have a real conversation about your growth strategy. No jargon, no guesswork, just clarity.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link 
              to="/contact" 
              className="inline-flex justify-center items-center space-x-2 bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors w-full sm:w-auto"
            >
              <span>Start the Conversation</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
            <Link 
              to="/services" 
              className="inline-flex justify-center items-center bg-white text-secondary-900 border border-secondary-200 px-8 py-4 rounded-lg font-semibold hover:bg-secondary-50 transition-colors w-full sm:w-auto"
            >
              Explore Our Services
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

export default About